import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { cx } from 'linaria';
import { RootReducer } from 'store/root-reducer';
import { getArticle } from 'common/selectors';
import { getCatalogue4LevelLink } from 'features/page-model';
import { getCartItem } from 'features/basket';
import { borderStyle } from './styles';
import { StickyPanel, StickyPanelProps } from './sticky-panel';
import { ObserverComponent } from './observer-component';
import { getDisplayValue } from './utils';
import { useActiveSection, Section } from './hooks';
import { ConnectedProps } from 'types/utils';

type StickyPanelMappedState = {
  quantity: number;
  catalogue4LevelLink: string;
  productId?: string;
};

type StickyPanelWithNavigationProps = Omit<
  StickyPanelProps,
  'sections' | 'activeSectionHref' | 'display' | 'isSet'
> & {
  sections: Section[];
} & ConnectedProps<StickyPanelMappedState>;

export const StickyPanelWithNavigation: React.FC<StickyPanelWithNavigationProps> = ({
  sections,
  className = '',
  productId,
  isSet,
  ...props
}) => {
  const { activeHref, setActiveHref, onChange } = useActiveSection(sections);

  const stickyPanelSections = useMemo(
    () => sections.map(({ id, title, badgeNumber }: Section) => ({ href: `#${id}`, title, badgeNumber })),
    [sections],
  );

  return (
    <>
      {sections.map((section: Section, index: number) => (
        <ObserverComponent
          key={section.id}
          id={section.id}
          index={index}
          total={sections.length}
          onChange={onChange}
        />
      ))}
      <StickyPanel
        data-qa="sticky-panel"
        activeSectionHref={activeHref}
        display={getDisplayValue({ breakpoint: props.breakpoint })}
        sections={stickyPanelSections}
        className={cx(className, borderStyle)}
        isSet={isSet}
        onSectionClick={setActiveHref}
        {...props}
      />
    </>
  );
};

const mapStateToProps = (store: RootReducer): StickyPanelMappedState => {
  const productId = getArticle(store) || '';
  const quantity = getCartItem(productId)(store)?.quantity || 0;
  const catalogue4LevelLink = getCatalogue4LevelLink(store);

  return { productId, quantity, catalogue4LevelLink };
};

export const StickyPanelConnected = connect(mapStateToProps)(StickyPanelWithNavigation);
